'use strict';

/**
 * Shorten — truncate HTML content with "show more / show less" toggle.
 * Vanilla JS replacement for jQuery Shorten plugin.
 *
 * Usage:
 *   import { shorten } from './helpers/shorten';
 *   shorten('.seria_info__bodycopy', { showChars: 300, moreText: 'виж още ...' });
 */

const defaults = {
    showChars: 100,
    minHideChars: 10,
    ellipsesText: '...',
    moreText: 'more',
    lessText: 'less',
};

/**
 * Truncate HTML-aware: counts visible characters while preserving tag structure.
 */
function truncateHTML(html, maxChars, ellipsis) {
    // If no HTML tags, simple substring
    if (html.indexOf('<') === -1) {
        return html.substring(0, maxChars) + ellipsis;
    }

    let inTag = false;
    let visibleCount = 0;
    let bag = '';
    const openTags = [];

    for (let i = 0; i < html.length; i++) {
        const ch = html[i];

        if (ch === '<' && !inTag) {
            inTag = true;

            // Extract tag name (could be "tag" or "/tag" or "br/")
            const closeIdx = html.indexOf('>', i);
            const tagContent = html.substring(i + 1, closeIdx);
            const tagName = tagContent.split(/[\s/]/)[0];

            if (tagContent[0] === '/') {
                // Closing tag
                const name = tagContent.substring(1).trim();
                if (openTags[0] === name) {
                    openTags.shift();
                }
            } else if (!tagContent.endsWith('/') && tagName.toLowerCase() !== 'br') {
                // Opening tag (not self-closing, not <br>)
                openTags.unshift(tagName);
            }
        }

        if (inTag && ch === '>') {
            inTag = false;
            bag += ch;
            continue;
        }

        if (inTag) {
            bag += ch;
        } else {
            if (visibleCount < maxChars) {
                bag += ch;
                visibleCount++;
            } else {
                // Close any open tags
                for (const tag of openTags) {
                    bag += '</' + tag + '>';
                }
                break;
            }
        }
    }

    return bag + '<span class="ellip">' + ellipsis + '</span>';
}

/**
 * Apply shorten to all elements matching `selector`.
 */
export function shorten(selector, options = {}) {
    const config = { ...defaults, ...options };

    document.querySelectorAll(selector).forEach((el) => {
        // Skip if already shortened
        if (el.dataset.shortened) return;
        el.dataset.shortened = 'true';

        const content = el.innerHTML;
        const textLength = el.textContent.length;

        if (textLength <= config.showChars + config.minHideChars) return;

        const truncated = truncateHTML(content, config.showChars, config.ellipsesText);

        el.innerHTML =
            '<div class="shortcontent">' + truncated + '</div>' +
            '<div class="allcontent" style="display:none">' + content + '</div>' +
            '<span><a href="#" class="morelink">' + config.moreText + '</a></span>';

        // Remove bottom margin on last paragraph of short content
        const lastP = el.querySelector('.shortcontent p:last-child');
        if (lastP) lastP.style.marginBottom = '0';
    });

    // Single delegated listener for more/less toggle (only bound once)
    if (!shorten._bound) {
        shorten._bound = true;

        document.addEventListener('click', (event) => {
            const link = event.target.closest('.morelink');
            if (!link) return;

            event.preventDefault();

            const wrapper = link.closest('[data-shortened]');
            if (!wrapper) return;

            const shortContent = wrapper.querySelector('.shortcontent');
            const allContent = wrapper.querySelector('.allcontent');

            if (link.classList.contains('less')) {
                // Collapse
                link.classList.remove('less');
                link.textContent = config.moreText;
                allContent.style.display = 'none';
                shortContent.style.display = '';
            } else {
                // Expand
                link.classList.add('less');
                link.textContent = config.lessText;
                shortContent.style.display = 'none';
                allContent.style.display = '';
            }
        });
    }
}
